import { Alert, Button, Chip, Stack, Typography } from '@mui/material';

import { deriveIntakeProgress } from '../../intakeProgress';
import type { IntakeHubProviderRosterEntry, IntakePracticeVerification } from '../../types';

/** Review remains in the staff form, which owns submission checks and verify/discard mutations. */
export function AdminIntakeVerification({
  providers,
  selectedUserId,
  rosterEntries,
  verification,
  onReview,
}: {
  providers: { id: string; label: string }[];
  selectedUserId: string;
  rosterEntries?: IntakeHubProviderRosterEntry[];
  verification?: IntakePracticeVerification | null;
  onReview: (userId: string) => void;
}) {
  const visible = selectedUserId ? providers.filter(item => item.id === selectedUserId) : providers;
  return (
    <Stack spacing={2}>
      <Typography variant="h5">JotPsych verification</Typography>
      <Typography color="text.secondary">
        Review each provider’s submitted intake. Confirm or discard verification from their intake
        review; verification is recorded separately for each provider.
      </Typography>
      {verification && (
        <Alert severity={verification.state === 'verified' ? 'success' : 'info'}>
          {verification.provider_counts.verified} of {verification.provider_counts.total} roster
          providers verified
          {' · '}
          {verification.provider_counts.awaiting} awaiting verification
          {' · '}
          {verification.provider_counts.incomplete} incomplete
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary">
        Agreements and EHR migration apply to the organization. Payer connections and credentialing
        records are available in Go Live.
      </Typography>
      {visible.length === 0 ? (
        <Alert severity="info">No practice members are available to review.</Alert>
      ) : (
        visible.map(provider => {
          const entry = rosterEntries?.find(row => row.user_id === provider.id);
          const progress = deriveIntakeProgress(entry?.progress_inputs);
          const status = !rosterEntries
            ? 'Status unavailable'
            : !entry
              ? 'Not in intake roster'
              : entry.journey_status?.verified_at
                ? 'Verified'
                : entry.journey_status?.submitted_at
                  ? 'Awaiting verification'
                  : entry.journeys_pending
                    ? 'Intake setup pending'
                    : 'Intake incomplete';
          return (
            <Stack key={provider.id} direction="row" sx={{ alignItems: 'center', gap: 2 }}>
              <Stack sx={{ flex: 1 }} spacing={0.5}>
                <Typography>{provider.label}</Typography>
                {progress && (
                  <Typography variant="caption">
                    Intake {progress.percentComplete}% complete
                  </Typography>
                )}
              </Stack>
              <Chip size="small" variant="outlined" label={status} />
              <Button variant="outlined" onClick={() => onReview(provider.id)}>
                Review intake for {provider.label}
              </Button>
            </Stack>
          );
        })
      )}
    </Stack>
  );
}
